import { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Container,
  Card,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FaHistory } from "react-icons/fa";
import moment from "moment";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import { useTypeConfig } from "../../../utils/useTypeConfig";

const TicketViewModal = ({ show, handleClose, ticketId, ticket }) => {
  const [ticketLogs, setTicketLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const { labels, hiddenFields } = useTypeConfig();

  useEffect(() => {
    if (!show || !ticketId) return;

    const fetchLogs = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/api/ticket-logs/${ticketId}/`,
          { headers: HEADERS },
        );
        setTicketLogs(res.data);
      } catch (error) {
        console.error("Error fetching ticket logs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [show, ticketId]);

  const getBadgeClass = (status) => {
    switch (status) {
      case "Completed":
        return "#0088FE";
      case "On-Hold":
        return "#fd7e14";
      case "Ongoing":
        return "#ffc107";
      case "Not-Assigned":
        return "#343a40";
      case "Accepted":
        return "#28a745";
      case "Inactive":
        return "#6c757d";
      case "Rejected":
        return "#dc3545";
      case "Autoclose":
        return "#800080";
      default:
        return "#343a40";
    }
  };

  const renderChanges = (changes) => {
    if (!changes || typeof changes !== "object") return "—";

    const validChanges = Object.entries(changes).filter(
      ([, v]) => v?.new !== null && v?.new !== "" && v?.new !== undefined,
    );

    if (validChanges.length === 0) return "—";

    return (
      <ul className="mb-0 ps-3">
        {validChanges.map(([field, value], idx) => (
          <li key={idx}>
            <strong>{field}</strong>:{" "}
            <span className="text-muted">{value.previous ?? "—"}</span> →{" "}
            <span className="text-success">{value.new}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      dialogClassName="custom-modern-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100">
          <div className="d-flex justify-content-between align-items-center">
            <span className="fw-semibold text-truncate">
              🎫 {ticket?.taskName || "N/A"}
            </span>

            <Badge
              style={{
                backgroundColor: getBadgeClass(ticket?.status),
                padding: "6px 12px",
              }}
            >
              {ticket?.status}
            </Badge>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading ticket details...</p>
          </div>
        ) : (
          <Container fluid>
            <Card className="mb-3 shadow-sm">
              <Card.Body className="small">
                <Row>
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Created At:</strong>{" "}
                      {moment(ticket?.created_at).format("DD-MM-YYYY") || "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Created By:</strong> {ticket?.created_by || "—"}
                    </p>
                    <p className="mb-1">
                      <strong>{labels.customerName}:</strong>{" "}
                      {ticket?.customerDetails?.customer_name || "—"}
                    </p>
                    <p className="mb-1">
                      <strong>{labels.subCustomerName}:</strong>{" "}
                      {ticket?.subCustomerDetails?.customer_name || "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Service:</strong>{" "}
                      {ticket?.serviceDetails?.service_name || "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Region:</strong> {ticket?.region || "—"}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Assigned To:</strong>{" "}
                      {ticket?.assignTo
                        ? `${ticket.assignTo.first_name} ${ticket.assignTo.last_name}`
                        : "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Phone:</strong> {ticket?.phone_number || "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Purpose:</strong> {ticket?.purpose || "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Instructions:</strong>{" "}
                      {ticket?.instructions || "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Address:</strong> {ticket?.ticket_address || "—"}
                    </p>

                    {!hiddenFields.includes("productModel") && (
                      <p className="mb-1">
                        <strong>Product:</strong> {ticket?.brand?.name || "—"}{" "}
                        {ticket?.model ? `(${ticket.model})` : ""}
                      </p>
                    )}

                    <p className="mb-1">
                      <strong>AMC / Rate:</strong>{" "}
                      {ticket?.selected_amc?.amcName || ticket?.rate || "—"}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            <Card className="shadow-sm">
              <Card.Body>
                <h5 className="text-primary mb-3">
                  <FaHistory className="me-2" />
                  Ticket Activity Log
                </h5>

                {ticketLogs?.logs?.length > 0 ? (
                  <div style={{ overflowX: "auto" }}>
                    <table className="inventory-table w-100">
                      <thead>
                        <tr>
                          <th style={{ minWidth: "150px" }}>Updated By</th>
                          <th style={{ minWidth: "160px" }}>Date & Time</th>
                          <th style={{ minWidth: "120px" }}>Action</th>
                          <th style={{ minWidth: "280px" }}>Changes</th>
                        </tr>
                      </thead>

                      <tbody>
                        {ticketLogs.logs.map((log, idx) => (
                          <tr key={idx}>
                            <td>{log.updated_by || "—"}</td>

                            <td>
                              {moment(log.updated_on).format(
                                "DD-MM-YYYY hh:mm A",
                              )}
                            </td>

                            <td>
                              <Badge bg="secondary">{log.action}</Badge>
                            </td>

                            <td>
                              <div style={{ maxWidth: "350px" }}>
                                {renderChanges(log.changes)}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted text-center mb-0">
                    No ticket activity available.
                  </p>
                )}
              </Card.Body>
            </Card>
          </Container>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TicketViewModal;
